import { z } from 'zod'
import { PlatformType } from '../types'

const baseFields = {
  shipperName: z.string().min(1, '최대 20자까지 입력할 수 있어요.').max(20, '최대 20자까지 입력할 수 있어요.'),
  shipperCode: z.string().optional(),
  companyName: z.string().min(1, '최대 30자까지 입력할 수 있어요.').max(30, '최대 30자까지 입력할 수 있어요.'),
  businessRegistrationNumber: z
    .string()
    .min(1, '최대 20자까지 입력할 수 있어요.')
    .regex(/^[0-9]+$/, '숫자만 입력해 주세요.')
    .max(20, '최대 20자까지 입력할 수 있어요.'),
  ownerName: z.string().min(1, '최대 10자까지 입력할 수 있어요.').max(10, '최대 10자까지 입력할 수 있어요.'),
}

const baseUpdateFields = {
  shipperCode: z.string(),
  shipperName: baseFields.shipperName.optional(),
  companyName: baseFields.companyName.optional(),
  ownerName: baseFields.ownerName.optional(),
  businessRegistrationNumber: baseFields.businessRegistrationNumber,
}

/** 언더스코어까지만 허용하던 종전 규칙. FF OFF 일 때 쓴다 (LOGISPRD-10474). */
const COMPANY_ID_LEGACY_PATTERN = /^[a-zA-Z0-9_]+$/
const COMPANY_ID_LEGACY_MESSAGE = '업체아이디는 영문, 숫자, 언더스코어(_)만 입력할 수 있어요.'

/**
 * 특수문자를 연 규칙. FF ON 일 때 쓴다.
 *
 *     ~ - . [ ] { } : ! @ # $ % ^ & * ( ) + = _
 *
 * 한글·공백과 " ' , / ; < > ? \ ` | 는 여전히 막는다.
 */
const COMPANY_ID_EXTENDED_PATTERN = /^[a-zA-Z0-9~\-.[\]{}:!@#$%^&*()+=_]+$/
const COMPANY_ID_EXTENDED_MESSAGE = '업체아이디는 영문, 숫자, 특수문자(~ - . [ ] { } : ! @ # $ % ^ & * ( ) + = _)만 입력할 수 있어요.'

export const createSalesPlatformSchema = (allowSpecialChars: boolean) => {
  const pattern = allowSpecialChars ? COMPANY_ID_EXTENDED_PATTERN : COMPANY_ID_LEGACY_PATTERN
  const message = allowSpecialChars ? COMPANY_ID_EXTENDED_MESSAGE : COMPANY_ID_LEGACY_MESSAGE

  return z
    .object({
      type: z.string(),
      name: z.string(),
      companyId: z.string().optional(),
      usable: z.boolean().optional(),
      isBoutique: z.boolean().optional(),
      masterCompanyId: z.number().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.type === 'MANAGED') {
        if (!data.companyId || data.companyId.trim() === '' || !pattern.test(data.companyId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['companyId'],
            message,
          })
        }
      }
    })
}

/** 타입 추론과 FF 를 읽지 않는 호출부가 쓰는 기본 인스턴스. fail-safe 로 종전 규칙이다. */
export const salesPlatformSchema = createSalesPlatformSchema(false)

const optionalFields = {
  businessType: z
    .string()
    .max(10, '최대 10자까지 입력 가능합니다')
    .optional()
    .transform((val) => (val === '' ? undefined : val)),
  businessMajor: z
    .string()
    .max(10, '최대 10자까지 입력 가능합니다')
    .optional()
    .transform((val) => (val === '' ? undefined : val)),
  zipCode: z
    .string()
    .optional()
    .refine((val) => !val || val === '' || /^[0-9]{5}$/.test(val), '우편번호는 5자리 숫자로 입력해 주세요.')
    .transform((val) => (val === '' ? undefined : val)),
  address: z
    .string()
    .max(100, '최대 100자까지 입력할 수 있어요.')
    .optional()
    .transform((val) => (val === '' ? undefined : val)),
  phoneNumber: z
    .string()
    .optional()
    .refine((val) => !val || val === '' || /^[0-9-]+$/.test(val), '숫자와 하이픈(-)만 입력해 주세요.')
    .transform((val) => (val === '' ? undefined : val)),
  mfsTermsAgreedAt: z.string().optional(),
}

const createSalesPlatformsArraySchema = (allowSpecialChars: boolean) =>
  z.array(createSalesPlatformSchema(allowSpecialChars)).superRefine((platforms, ctx) => {
    const validPlatforms = platforms.filter((platform) => platform.type && platform.type.trim() !== '')

    if (validPlatforms.length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '최소 1개 이상의 판매플랫폼을 등록해야 합니다.',
      })
    }

    // 중복 플랫폼 검증
    const platformMap = new Map<string, number[]>()

    for (let i = 0; i < platforms.length; i++) {
      const platform = platforms[i]

      let key: string

      if (platform.type === 'MANAGED') {
        key = platform.name
      } else {
        key = platform.type
      }

      if (!platformMap.has(key)) {
        platformMap.set(key, [])
      }
      platformMap.get(key)!.push(i)
    }

    // 중복된 플랫폼들에 대해 에러 추가
    for (const [key, indices] of platformMap.entries()) {
      if (key !== '' && indices.length > 1) {
        for (const index of indices) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [index, 'type'],
            message: '중복된 플랫폼이 있습니다. 사용안하는 플랫폼이 있다면 제거해주세요.',
          })
        }
      }
    }
  })

const baseCreateSchema = z.object({
  ...baseFields,
  ...optionalFields,
})

export const createMwpShipperCreateSchema = (allowSpecialChars: boolean) =>
  baseCreateSchema.extend({
    logisticsServiceType: z.literal('MWP'),
    salesPlatforms: createSalesPlatformsArraySchema(allowSpecialChars),
  })

export const createMltShipperCreateSchema = (allowSpecialChars: boolean) =>
  baseCreateSchema.extend({
    logisticsServiceType: z.literal('MLT'),
    salesPlatforms: createSalesPlatformsArraySchema(allowSpecialChars),
  })

export const mwpShipperCreateSchema = createMwpShipperCreateSchema(false)

export const mltShipperCreateSchema = createMltShipperCreateSchema(false)

export const mcdShipperCreateSchema = baseCreateSchema.extend({
  logisticsServiceType: z.literal('MCD'),
})

const baseUpdateSchema = z.object({
  ...baseUpdateFields,
  ...optionalFields,
})

export const createMwpShipperUpdateSchema = (allowSpecialChars: boolean) =>
  baseUpdateSchema.extend({
    logisticsServiceType: z.literal('MWP'),
    salesPlatforms: createSalesPlatformsArraySchema(allowSpecialChars),
  })

export const createMltShipperUpdateSchema = (allowSpecialChars: boolean) =>
  baseUpdateSchema.extend({
    logisticsServiceType: z.literal('MLT'),
    salesPlatforms: createSalesPlatformsArraySchema(allowSpecialChars),
  })

export const createMfsShipperUpdateSchema = (allowSpecialChars: boolean) =>
  z.object({
    shipperCode: z.string(),
    businessRegistrationNumber: z.string(), // MEMO: 이미 존재하는 화주에 경우 사업자등록번호에대해 valid한지 판단하지 않음 https://jira.team.musinsa.com/browse/LOGISPRD-3447?focusedCommentId=669455
    shipperName: z.string().optional(),
    companyName: z.string().optional(),
    ownerName: z.string().optional(),
    logisticsServiceType: z.literal('MFS'),
    salesPlatforms: createSalesPlatformsArraySchema(allowSpecialChars).optional(),
    ...optionalFields,
  })

export const mwpShipperUpdateSchema = createMwpShipperUpdateSchema(false)

export const mltShipperUpdateSchema = createMltShipperUpdateSchema(false)

export const mfsShipperUpdateSchema = createMfsShipperUpdateSchema(false)

export const mcdShipperUpdateSchema = baseUpdateSchema.extend({
  logisticsServiceType: z.literal('MCD'),
})

export const salesPlatformDeleteSchema = z.object({
  type: z.string(),
  name: z.string(),
  companyId: z.string().optional(),
})

export const salesPlatformUpdateSchema = z.object({
  masterCompanyId: z.number(),
  newSalesPlatformName: z.string().optional(),
  newSalesPlatformType: z.string().optional(),
  newCompanyId: z.string().optional(),
  usable: z.boolean().optional(),
})

export const shipperCreateSchema = z.discriminatedUnion('logisticsServiceType', [
  mwpShipperCreateSchema,
  mltShipperCreateSchema,
  mcdShipperCreateSchema,
])

export const shipperUpdateSchema = z.discriminatedUnion('logisticsServiceType', [
  mwpShipperUpdateSchema,
  mltShipperUpdateSchema,
  mfsShipperUpdateSchema,
  mcdShipperUpdateSchema,
])
export type MWPShipperCreateForm = z.infer<typeof mwpShipperCreateSchema>
export type MLTShipperCreateForm = z.infer<typeof mltShipperCreateSchema>
export type MCDShipperCreateForm = z.infer<typeof mcdShipperCreateSchema>
export type ShipperCreateForm = z.infer<typeof shipperCreateSchema>

export type MWPShipperUpdateForm = z.infer<typeof mwpShipperUpdateSchema>
export type MLTShipperUpdateForm = z.infer<typeof mltShipperUpdateSchema>
export type MFSShipperUpdateForm = z.infer<typeof mfsShipperUpdateSchema>
export type MCDShipperUpdateForm = z.infer<typeof mcdShipperUpdateSchema>
export type ShipperUpdateForm = z.infer<typeof shipperUpdateSchema>

export const shipperSchema = z.union([
  mwpShipperCreateSchema,
  mltShipperCreateSchema,
  mcdShipperCreateSchema,
  mwpShipperUpdateSchema,
  mltShipperUpdateSchema,
  mfsShipperUpdateSchema,
  mcdShipperUpdateSchema,
])

export type ShipperForm = z.infer<typeof shipperSchema>
